import { useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '../utils/logger';

const WARNING_TIME_MS = 5 * 60 * 1000;
const CHECK_INTERVAL_MS = 30 * 1000;

interface SessionMonitorState {
  isExpiringSoon: boolean;
  timeRemaining: number;
}

export function useSessionMonitor(isAuthenticated: boolean) {
  const [state, setState] = useState<SessionMonitorState>({
    isExpiringSoon: false,
    timeRemaining: 0,
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const warningShownRef = useRef(false);

  const checkTokenExpiration = useCallback(() => {
    if (!isAuthenticated) {
      setState({ isExpiringSoon: false, timeRemaining: 0 });
      warningShownRef.current = false;
      return;
    }

    const expiresAtStr = localStorage.getItem('senciyo_expires_at');
    if (!expiresAtStr) return;

    const expiresAt = parseInt(expiresAtStr, 10);
    const now = Date.now();
    const timeRemaining = expiresAt - now;

    setState(prev => ({
      ...prev,
      timeRemaining,
    }));

    if (timeRemaining <= WARNING_TIME_MS && timeRemaining > 0 && !warningShownRef.current) {
      logger.info('Token expiring soon', { timeRemaining: Math.floor(timeRemaining / 1000) });
      setState(prev => ({
        ...prev,
        isExpiringSoon: true,
      }));
      warningShownRef.current = true;
    }

    if (timeRemaining <= 0) {
      logger.warn('Token expired');
      setState({ isExpiringSoon: false, timeRemaining: 0 });
      warningShownRef.current = false;
    }
  }, [isAuthenticated]);

  const dismissWarning = useCallback(() => {
    setState(prev => ({
      ...prev,
      isExpiringSoon: false,
    }));
    warningShownRef.current = true;
  }, []);

  const resetWarning = useCallback(() => {
    warningShownRef.current = false;
    setState(prev => ({
      ...prev,
      isExpiringSoon: false,
    }));
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    checkTokenExpiration();

    intervalRef.current = setInterval(checkTokenExpiration, CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAuthenticated, checkTokenExpiration]);

  return {
    isExpiringSoon: state.isExpiringSoon,
    timeRemaining: state.timeRemaining,
    dismissWarning,
    resetWarning,
  };
}
